import { makeAutoObservable, runInAction } from 'mobx'
import { getEntries, logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import {
  type IIntegrationKey,
  IntegrationKey,
  IntegrationsApi,
  type IntegrationsStore,
} from 'entities/Integrations'
import { MergeField } from 'entities/MergeField/model/MergeField'
import { FieldLoaderRegistryImpl } from '../fieldLoaders'

export class MergeFieldStore {
  private _loading = false
  private _mergeFieldsIntegrationsMap = new Map<IIntegrationKey, Map<string, MergeField>>()
  private _fieldLoaderRegistryImpl: FieldLoaderRegistryImpl

  constructor(private _integrationsStore: IntegrationsStore) {
    this._fieldLoaderRegistryImpl = new FieldLoaderRegistryImpl(this._integrationsStore)

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get mergeFieldsIntegrationsMap() {
    return this._mergeFieldsIntegrationsMap
  }

  get mergeFieldsIntegrationsList() {
    return Array.from(this._mergeFieldsIntegrationsMap.values()).flatMap((integration) => {
      return Array.from(integration.values())
    })
  }

  get allFields(): MergeField[] {
    let fields: MergeField[] = []
    this._mergeFieldsIntegrationsMap.forEach((map) => {
      if (map) {
        fields = [...fields, ...Array.from(map.values())]
      }
    })
    return fields.filter((field) =>
      field.integrationKey === IntegrationKey.salesmessage
        ? true
        : !!this._integrationsStore.getIntegration(field.integrationKey)?.isConnected
    )
  }

  getIntegrationFields = async (key: IIntegrationKey) => {
    const integration = this._mergeFieldsIntegrationsMap.get(key)
    if (integration && integration.size) {
      return Array.from(integration.values())
    }
    const map = await this.updateIntegrationFields(key)
    return Array.from(map?.values() || [])
  }

  isMergeField = (match: string) => {
    const integrationKeyList = Object.values(IntegrationKey)

    integrationKeyList.forEach((key) => {
      if (match.includes(key)) return true
    })

    return this.mergeFieldsIntegrationsList.find((mergeField) => {
      const matchKey = match.split(' ')?.[1]
      if (!matchKey) return false
      return mergeField.template === `{ ${matchKey} }`
    })
  }

  updateIntegrationFields = async (key: IIntegrationKey) => {
    try {
      this._loading = true

      const loader = this._fieldLoaderRegistryImpl.getLoader(key)
      if (!loader) {
        logger.error(`No field loader found for integration ${key}`)
        return
      }

      const fields = await loader.loadAllFields()
      const map = new Map(
        fields.map((field) => {
          const newField = new MergeField(field)
          // Use consistent key generation logic with _loadCustomIntegrations
          const uniqueKey = newField.referencedObjectType
            ? `${newField.name}_${newField.referencedObjectType}`
            : newField.name
          return [uniqueKey, newField]
        })
      )

      runInAction(() => {
        this._mergeFieldsIntegrationsMap.set(key, map)
      })
      return map
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _loadAllFields = async () => {
    try {
      this._loading = true

      runInAction(() => {
        this._mergeFieldsIntegrationsMap.clear()
      })

      // Load both generic and custom integrations in parallel
      return await Promise.allSettled([
        this._loadGenericIntegrations(),
        this._loadCustomIntegrations(),
      ])
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _loadGenericIntegrations = async () => {
    try {
      const { data } = await IntegrationsApi.getAllFields()

      runInAction(() => {
        getEntries(data).forEach(([key, fields]) => {
          // Only load integrations that use generic API
          if (this._fieldLoaderRegistryImpl.getLoader(key)?.useGenericApi()) {
            const map = new Map(
              fields.map((field) => {
                const newField = new MergeField(field)
                return [newField.name, newField]
              })
            )
            this._mergeFieldsIntegrationsMap.set(key, map)
          }
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  addMergeFieldsIntegrations = (key: IntegrationKey, fields: MergeField[]) => {
    this._mergeFieldsIntegrationsMap.set(
      key,
      new Map(
        fields.map((field) => {
          return [field.name, field]
        })
      )
    )
  }

  removeMergeFieldsIntegrations = (key: IntegrationKey) => {
    this._mergeFieldsIntegrationsMap.delete(key)
  }

  getMergeFieldsIntegration = (key: IntegrationKey) => {
    return this._mergeFieldsIntegrationsMap.get(key)
  }

  private _loadCustomIntegrations = async () => {
    const customIntegrations = this._fieldLoaderRegistryImpl.getCustomIntegrations()

    // Load all custom integrations in parallel
    const results = await Promise.allSettled(
      customIntegrations.map(async (integrationKey) => {
        const loader = this._fieldLoaderRegistryImpl.getLoader(integrationKey)
        if (!loader) return null

        try {
          const fields = await loader.loadAllFields()
          return { integrationKey, fields }
        } catch (e) {
          logger.error(e)
          return null
        }
      })
    )

    // Process successful results
    runInAction(() => {
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const { integrationKey, fields } = result.value
          const map = new Map(
            fields.map((field) => {
              const newField = new MergeField(field)
              // Create unique key to handle duplicate field names across different groups
              // e.g., "LastName" becomes "LastName_contact" and "LastName_lead"
              const uniqueKey = newField.referencedObjectType
                ? `${newField.name}_${newField.referencedObjectType}`
                : newField.name
              return [uniqueKey, newField]
            })
          )
          this._mergeFieldsIntegrationsMap.set(integrationKey, map)
        }
      })
    })
  }

  loadAllFields = new SingletonRequest(this._loadAllFields).request

  getMergeFieldInfoByTemplate = (
    template: string
  ): {
    mergeField: MergeField | null
    fallback: string
    mergeFieldKey: string
    integrationKey: string
    fullMergeField: string
  } => {
    const newText = template.replace(/{|}/g, '').trim()
    const split = newText.split('|')
    const fullMergeField = split[0].trim()
    const fallback = (split[1] || '').replace(/&#39;/g, '').trim()
    const splitKey = fullMergeField.split('.')
    const integrationKey = Object.values(IntegrationKey).includes(splitKey[0] as IntegrationKey)
      ? splitKey[0]
      : IntegrationKey.salesmessage

    const mergeFieldKey = (splitKey[1] || splitKey[0] || '').trim()
    const integrationMergeFieldMap =
      integrationKey && this._mergeFieldsIntegrationsMap.get(integrationKey as IntegrationKey)
    const mergeField =
      integrationMergeFieldMap &&
      (integrationMergeFieldMap.get(mergeFieldKey) || integrationMergeFieldMap.get(fullMergeField))

    return {
      mergeField: mergeField || null,
      fallback,
      mergeFieldKey,
      integrationKey,
      fullMergeField,
    }
  }
}
